import { authenticatedFetch } from "@lumi/data";
import { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

/**
 * TMDB Movie search result
 */
export interface TMDBSearchResult {
  id: number;
  title: string;
  release_date: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  vote_average: number;
}

/**
 * TMDB Movie details
 */
export interface TMDBMovieDetails {
  id: number;
  title: string;
  release_date: string;
  overview: string;
  runtime: number;
  original_language?: string;
  production_countries?: Array<{ iso_3166_1: string; name: string }>;
  genres: Array<{ id: number; name: string }>;
  poster_path: string | null;
  backdrop_path: string | null;
  vote_average: number;
  credits?: {
    cast: Array<{ id: number; name: string; character: string; profile_path: string | null }>;
    crew: Array<{ id: number; name: string; job: string; department: string }>;
  };
}

/**
 * TMDB Images response
 */
export interface TMDBImages {
  posters: Array<{ file_path: string; vote_average: number; width: number; height: number }>;
  backdrops: Array<{ file_path: string; vote_average: number; width: number; height: number }>;
}

/**
 * Hook for searching TMDB movies
 */
export function useTMDBSearch() {
  const { accessToken, refreshAuth } = useAuth();
  const [results, setResults] = useState<TMDBSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const search = async (query: string, year?: number) => {
    if (!accessToken || !query.trim()) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ query });
      if (year) {
        params.append("year", year.toString());
      }

      const data = await authenticatedFetch<{ results: TMDBSearchResult[] }>(
        `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/tmdb/search?${params.toString()}`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setResults(data.results);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setResults([]);
    setError(null);
  };

  return { results, loading, error, search, clear };
}

/**
 * Hook for fetching TMDB movie details
 */
export function useTMDBDetails() {
  const { accessToken, refreshAuth } = useAuth();
  const [data, setData] = useState<TMDBMovieDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = async (tmdbId: number): Promise<TMDBMovieDetails | null> => {
    if (!accessToken) {
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await authenticatedFetch<TMDBMovieDetails>(
        `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/tmdb/${tmdbId}`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setData(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setData(null);
    setError(null);
  };

  return { data, loading, error, fetch, clear };
}
